import React, { Component } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import Select from 'react-select';
import toastr from 'toastr';

import authAxios from './api';
import { defaulterrmsg } from './Functions';
import { formatMoney } from './feeStructure';

const menuStyles = { menuPortal: (base) => ({ ...base, zIndex: 10050 }) };
const semanticReviewEndpoint = (path) => {
  const configured = String(process.env.REACT_APP_SEMANTIC_REVIEW_API || '').replace(/\/+$/, '');
  if (configured) return `${configured}/legacy-fee-mapping/semantic-review/${path}`;
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `http://localhost:3012/legacy-fee-mapping/semantic-review/${path}`;
  }
  return `legacy-fee-mapping/semantic-review/${path}`;
};
const statusLabels = {
  open: ['Needs review', 'warning', 'bg-warning text-dark'],
  draft: ['Draft saved', 'primary', 'bg-primary text-white'],
  approved: ['Approved', 'info', 'bg-info text-dark'],
  finalized: ['Saved permanently', 'success', 'bg-success text-white'],
};
const severityLabels = {
  blocking: ['Must resolve', 'danger', 'bg-danger text-white'],
  high: ['Important', 'warning', 'bg-warning text-dark'],
  medium: ['Check', 'info', 'bg-info text-dark'],
  low: ['Low', 'secondary', 'bg-secondary text-white'],
};

const friendlyCategory = (value) => String(value || '')
  .toLowerCase()
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

class SemanticReviewPanel extends Component {
  state = {
    summary: null,
    rows: [],
    loading: true,
    page: 1,
    totalPages: 1,
    total: 0,
    search: '',
    status: '',
    severity: '',
    category: '',
    activeReview: null,
    editor: { resolution_code: '', decision_payload: {}, review_notes: '' },
    working: '',
    studentSearch: '',
    studentResults: [],
    searchingStudents: false,
    selectedReviewIds: [],
    batchModal: false,
    batchEditor: { resolution_code: '', review_notes: '' },
  };

  componentDidMount() {
    this.reload();
  }

  componentDidUpdate(previousProps) {
    if (previousProps.mode !== this.props.mode || previousProps.orgId !== this.props.orgId) {
      this.setState({ page: 1, search: '', status: '', severity: '', category: '', activeReview: null, selectedReviewIds: [], batchModal: false }, this.reload);
    }
  }

  requestPayload = () => ({
    org_id: this.props.orgId,
    security_id: this.props.operatorId,
  });

  reload = async () => {
    await Promise.all([this.loadSummary(), this.props.mode === 'summary' ? Promise.resolve() : this.loadRows()]);
  };

  loadSummary = async () => {
    try {
      const { data } = await authAxios.post(semanticReviewEndpoint('summary'), this.requestPayload());
      this.setState({ summary: data, loading: false });
    } catch (error) {
      this.setState({ loading: false });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  loadRows = async (page = this.state.page) => {
    this.setState({ loading: true });
    try {
      const { data } = await authAxios.post(semanticReviewEndpoint('items'), {
        ...this.requestPayload(),
        review_group: this.props.mode,
        review_status: this.state.status,
        severity: this.state.severity,
        category: this.state.category,
        search: this.state.search,
        page,
        limit: 25,
      });
      this.setState({ rows: data.rows || [], total: data.total || 0, page: data.page || 1, totalPages: data.total_pages || 1, loading: false, selectedReviewIds: [] });
    } catch (error) {
      this.setState({ loading: false });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  openReview = (review) => this.setState({
    activeReview: review,
    editor: {
      resolution_code: review.resolution_code || (review.evidence?.student_match?.target_source_roll ? 'ACCEPT_RECOMMENDED_STUDENT_MATCH' : ''),
      decision_payload: review.decision_payload || {},
      review_notes: review.review_notes || '',
    },
    studentSearch: '',
    studentResults: [],
  });

  selectedResolution = () => {
    const review = this.state.activeReview;
    return (review?.resolution_options || []).find((option) => option.value === this.state.editor.resolution_code) || null;
  };

  updateDecisionPayload = (key, value) => this.setState((state) => ({
    editor: {
      ...state.editor,
      decision_payload: { ...state.editor.decision_payload, [key]: value },
    },
  }));

  chooseResolution = (choice) => this.setState((state) => ({
    editor: {
      ...state.editor,
      resolution_code: choice?.value || '',
      decision_payload: {},
    },
    studentSearch: '',
    studentResults: [],
  }));

  searchStudents = async () => {
    if (this.state.studentSearch.trim().length < 2) return toastr.error('Enter at least two letters or numbers.');
    this.setState({ searchingStudents: true });
    try {
      const { data } = await authAxios.post(semanticReviewEndpoint('students'), {
        ...this.requestPayload(),
        search: this.state.studentSearch,
      });
      this.setState({ studentResults: data || [], searchingStudents: false });
    } catch (error) {
      this.setState({ searchingStudents: false });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  saveDraft = async () => {
    const review = this.state.activeReview;
    this.setState({ working: 'draft' });
    try {
      await authAxios.post(semanticReviewEndpoint('draft'), {
        ...this.requestPayload(),
        review_id: review.review_id,
        evidence_checksum: review.evidence_checksum,
        resolution_code: this.state.editor.resolution_code,
        decision_payload: this.state.editor.decision_payload,
        review_notes: this.state.editor.review_notes,
        updated_by: this.props.operatorId,
      });
      toastr.success('Decision saved as draft.');
      this.setState({ activeReview: null, working: '' });
      await this.reload();
    } catch (error) {
      this.setState({ working: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  lifecycle = async (action) => {
    const review = this.state.activeReview;
    const message = action === 'approve'
      ? 'Approve this administrator decision after checking its evidence?'
      : action === 'reopen'
        ? 'Reopen this approved decision for changes?'
        : 'Save this decision permanently in the protected archive? It will become a constitutional input to future finance migrations.';
    if (!window.confirm(message)) return;
    this.setState({ working: action });
    try {
      await authAxios.post(semanticReviewEndpoint(action), {
        ...this.requestPayload(),
        review_id: review.review_id,
        updated_by: this.props.operatorId,
      });
      toastr.success(action === 'finalize' ? 'Decision saved permanently.' : action === 'approve' ? 'Decision approved.' : 'Decision reopened.');
      this.setState({ activeReview: null, working: '' });
      await this.reload();
    } catch (error) {
      this.setState({ working: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  selectedRows = () => {
    const selected = new Set(this.state.selectedReviewIds);
    return this.state.rows.filter((row) => selected.has(row.review_id));
  };

  commonBatchOptions = () => {
    const rows = this.selectedRows();
    if (!rows.length) return [];
    const allowedByRow = rows.map((row) => new Set((row.batch_resolution_options || []).map((option) => option.value)));
    return (rows[0].batch_resolution_options || []).filter((option) => allowedByRow.every((allowed) => allowed.has(option.value)));
  };

  batchItems = () => this.selectedRows().map((row) => ({
    review_id: row.review_id,
    evidence_checksum: row.evidence_checksum,
  }));

  toggleReviewSelection = (reviewId) => this.setState((state) => ({
    selectedReviewIds: state.selectedReviewIds.includes(reviewId)
      ? state.selectedReviewIds.filter((value) => value !== reviewId)
      : [...state.selectedReviewIds, reviewId],
  }));

  togglePageSelection = () => this.setState((state) => {
    const eligible = state.rows
      .filter((row) => row.review_status !== 'finalized' && (row.batch_resolution_options || []).length)
      .map((row) => row.review_id);
    const allSelected = eligible.length > 0 && eligible.every((reviewId) => state.selectedReviewIds.includes(reviewId));
    return { selectedReviewIds: allSelected ? [] : eligible };
  });

  openBatchDraft = () => {
    const options = this.commonBatchOptions();
    if (!options.length) return toastr.error('Select records that share one safe identity decision. Financial exceptions stay individual.');
    this.setState({ batchModal: true, batchEditor: { resolution_code: options[0].value, review_notes: '' } });
  };

  saveBatchDraft = async () => {
    this.setState({ working: 'batch-draft' });
    try {
      const { data } = await authAxios.post(semanticReviewEndpoint('batch-draft'), {
        ...this.requestPayload(),
        items: this.batchItems(),
        resolution_code: this.state.batchEditor.resolution_code,
        review_notes: this.state.batchEditor.review_notes,
        updated_by: this.props.operatorId,
      });
      toastr.success(data?.message || 'Selected decisions saved as drafts.');
      this.setState({ working: '', batchModal: false, selectedReviewIds: [] });
      await this.reload();
    } catch (error) {
      this.setState({ working: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
    }
  };

  batchLifecycle = async (action) => {
    const rows = this.selectedRows();
    const verb = action === 'approve' ? 'approve' : 'save permanently';
    if (!rows.length || !window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${rows.length} separately recorded decisions after checking this page?`)) return;
    this.setState({ working: `batch-${action}` });
    try {
      const { data } = await authAxios.post(semanticReviewEndpoint(`batch-${action}`), {
        ...this.requestPayload(),
        items: this.batchItems(),
        updated_by: this.props.operatorId,
      });
      toastr.success(data?.message || `${rows.length} decisions updated.`);
      this.setState({ working: '', selectedReviewIds: [] });
      await this.reload();
    } catch (error) {
      this.setState({ working: '' });
      toastr.error(error?.response?.data?.message || defaulterrmsg);
      await this.reload();
    }
  };

  renderSummary = () => {
    const summary = this.state.summary;
    if (!summary) return null;
    const complete = summary.status_counts?.finalized || 0;
    const matchInventory = summary.student_match_inventory;
    return (
      <>
        <Row className="g-3 mb-3">
          <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Review records</div><h3 className="mb-0">{summary.total || 0}</h3></CardBody></Card></Col>
          <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Still to complete</div><h3 className="mb-0 text-warning">{summary.remaining || 0}</h3></CardBody></Card></Col>
          <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Must resolve</div><h3 className="mb-0 text-danger">{summary.blocking_remaining || 0}</h3></CardBody></Card></Col>
          <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Saved permanently</div><h3 className="mb-0 text-success">{complete}</h3></CardBody></Card></Col>
        </Row>
        {this.props.mode === 'identity' && matchInventory && <>
          <Alert color="info">
            <strong>PHP student-ID history has been applied.</strong>{' '}
            Permanent IDs already in the student master are accepted as verified. Alternate IDs are proposed from the surviving PHP fields. Reused temporary IDs and DELETE are separated by bill and must never be matched as one shared student.
          </Alert>
          <Row className="g-3 mb-3">
            <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Verified PHP permanent IDs</div><h3 className="mb-0 text-success">{matchInventory.verified_permanent_id || 0}</h3><div className="small text-muted">Preserved as active or historical identity</div></CardBody></Card></Col>
            <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Alternate-ID matches</div><h3 className="mb-0 text-primary">{matchInventory.automatic_alternate_id || 0}</h3><div className="small text-muted">Can be confirmed together</div></CardBody></Card></Col>
            <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Name suggestions</div><h3 className="mb-0 text-info">{matchInventory.suggested_name || 0}</h3><div className="small text-muted">Check individually</div></CardBody></Card></Col>
            <Col xl={3} sm={6}><Card className="h-100 mb-0"><CardBody><div className="small text-muted">Manual matches</div><h3 className="mb-0 text-danger">{matchInventory.manual || 0}</h3><div className="small text-muted">Includes bill-level placeholders</div></CardBody></Card></Col>
          </Row>
        </>}
        {this.props.mode === 'summary' && (
          <>
            <Alert color={summary.migration_ready ? 'success' : 'warning'} className={summary.migration_ready ? '' : 'text-dark'}>
              <strong>{summary.migration_ready ? 'Semantic review is complete.' : 'Migration is not ready yet.'}</strong>{' '}
              Final readiness requires every review decision and historical fee mapping to be permanently saved, plus canonical fee definitions and installment amounts.
            </Alert>
            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Card className="h-100 mb-0"><CardBody>
                  <h5>Historical fee mappings</h5>
                  <Table size="sm" className="mb-0"><tbody>
                    <tr><td>Total started</td><td className="text-end">{summary.fee_mapping?.total || 0}</td></tr>
                    <tr><td>Draft</td><td className="text-end">{summary.fee_mapping?.draft || 0}</td></tr>
                    <tr><td>Approved</td><td className="text-end">{summary.fee_mapping?.approved || 0}</td></tr>
                    <tr><td>Saved permanently</td><td className="text-end text-success">{summary.fee_mapping?.applied || 0}</td></tr>
                  </tbody></Table>
                </CardBody></Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100 mb-0"><CardBody>
                  <h5>Canonical fee setup</h5>
                  <Table size="sm" className="mb-0"><tbody>
                    <tr><td>Active canonical definitions</td><td className="text-end">{summary.canonical_configuration?.active_definitions || 0}</td></tr>
                    <tr><td>Installment schedules</td><td className="text-end">{summary.canonical_configuration?.installment_schedules || 0}</td></tr>
                    <tr><td>Branch / cohort / year setups found</td><td className="text-end">{summary.canonical_configuration?.evidenced_periods || 0}</td></tr>
                    <tr><td>Complete setups</td><td className="text-end text-success">{summary.canonical_configuration?.complete_periods || 0}</td></tr>
                    <tr><td>Still missing fees or installments</td><td className="text-end text-warning">{summary.canonical_configuration?.incomplete_periods || 0}</td></tr>
                    <tr><td>Status</td><td className="text-end"><Badge color={summary.canonical_configuration?.complete ? 'success' : 'warning'} className={summary.canonical_configuration?.complete ? '' : 'text-dark'}>{summary.canonical_configuration?.complete ? 'Complete' : 'Needs setup'}</Badge></td></tr>
                  </tbody></Table>
                </CardBody></Card>
              </Col>
            </Row>
            {(summary.canonical_configuration?.missing_examples || []).length > 0 && <Card><CardBody>
              <h5 className="mb-1">Canonical fee setups still missing</h5>
              <div className="text-muted mb-3">Each row is one branch, cohort and study year used by at least one student. Complete it in Add / Modify Fee Types and Branch Fee.</div>
              <Table responsive hover size="sm" className="mb-0">
                <thead className="table-light"><tr><th>Branch</th><th>Cohort</th><th>Study year</th><th>Missing</th></tr></thead>
                <tbody>{summary.canonical_configuration.missing_examples.map((row) => (
                  <tr key={`${row.branch_id}:${row.calendar_years_id}:${row.academic_years_id}`}><td>{row.branch_name || 'Unknown branch'}</td><td>{row.calendar_year || row.calendar_years_id}</td><td>{row.academic_year || `Year ${row.study_year || '?'}`}</td><td>{(row.missing || []).join(', ')}</td></tr>
                ))}</tbody>
              </Table>
              {Number(summary.canonical_configuration?.incomplete_periods || 0) > summary.canonical_configuration.missing_examples.length && <div className="small text-muted mt-2">Showing the first {summary.canonical_configuration.missing_examples.length} incomplete setups. Finish these and refresh to continue.</div>}
            </CardBody></Card>}
            <Card><CardBody>
              <h5 className="mb-3">Remaining work by category</h5>
              <Table responsive hover size="sm" className="mb-0">
                <thead className="table-light"><tr><th>Review category</th><th className="text-end">Total</th><th className="text-end">Saved</th><th className="text-end">Remaining</th></tr></thead>
                <tbody>{(summary.categories || []).map((row) => (
                  <tr key={row.category}><td>{friendlyCategory(row.category)}</td><td className="text-end">{row.total}</td><td className="text-end text-success">{row.finalized}</td><td className="text-end text-warning">{row.remaining}</td></tr>
                ))}</tbody>
              </Table>
            </CardBody></Card>
          </>
        )}
      </>
    );
  };

  renderEvidenceValue = (key, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (key.toLowerCase().includes('minor') && Number.isFinite(Number(value))) return formatMoney(Number(value) / 100);
    if (Array.isArray(value)) return value.length ? value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  renderEvidence = (review) => {
    const identity = review.evidence?.identity;
    const transaction = review.evidence?.transaction;
    const studentMatch = review.evidence?.student_match;
    if (identity) {
      return (
        <Row className="g-2">
          <Col md={studentMatch ? 4 : 6}><div className="border rounded p-2 h-100"><div className="small text-muted">ID saved in PHP collections</div><strong>{identity.source_roll || review.subject}</strong><div className="small">{(identity.source_names || []).join(' / ') || 'Name unavailable'}</div></div></Col>
          {studentMatch ? <Col md={4}><div className={`border rounded p-2 h-100 ${studentMatch.target_source_roll ? 'border-success' : 'border-warning'}`}><div className="small text-muted">Recommended permanent ID</div><strong className={studentMatch.target_source_roll ? 'text-success' : 'text-warning'}>{studentMatch.target_source_roll || 'No proven match'}</strong><div className="small">{studentMatch.matched_identifier_fields?.length ? `Matched by ${studentMatch.matched_identifier_fields.map(friendlyCategory).join(', ')}` : friendlyCategory(studentMatch.resolution_method)}</div></div></Col> : <Col md={3}><div className="border rounded p-2 h-100"><div className="small text-muted">Branch / cohort</div><strong>{identity.source_branch || 'Unknown'}</strong><div className="small">{identity.cohort_code || 'Unknown cohort'}</div></div></Col>}
          <Col md={studentMatch ? 4 : 3}><div className="border rounded p-2 h-100"><div className="small text-muted">Active collections</div><strong>{transaction?.active_transaction_count || 0}</strong><div className="small">{formatMoney(Number(transaction?.active_amount_minor || 0) / 100)}</div></div></Col>
          {studentMatch && <Col xs={12}><div className="border rounded p-2"><div className="small text-muted">Bills kept inside this decision</div><strong>{(studentMatch.bill_numbers || []).join(', ') || 'Bill number unavailable'}</strong>{studentMatch.source_bill_scope && <div className="small text-danger">This is a bill-level match because the source ID cannot safely identify one student globally.</div>}</div></Col>}
        </Row>
      );
    }
    return (
      <Table bordered size="sm" className="mb-0 bg-white">
        <tbody>{Object.entries(review.evidence || {}).map(([key, value]) => (
          <tr key={key}><th style={{ width: '38%' }}>{friendlyCategory(key)}</th><td style={{ wordBreak: 'break-word' }}>{this.renderEvidenceValue(key, value)}</td></tr>
        ))}</tbody>
      </Table>
    );
  };

  renderDecisionField = (field) => {
    const payload = this.state.editor.decision_payload || {};
    if (field.type === 'student') {
      const selected = payload.target_student_snapshot;
      return (
        <div key={field.key} className="mt-3">
          <Label className="fw-bold">{field.label}</Label>
          {selected && <Alert color="success" className="py-2"><strong>{selected.display_id}</strong> — {selected.student_name}</Alert>}
          <div className="d-flex gap-2"><Input value={this.state.studentSearch} onChange={(event) => this.setState({ studentSearch: event.target.value })} placeholder="Search roll number or student name" /><Button color="secondary" outline disabled={this.state.searchingStudents} onClick={this.searchStudents}>{this.state.searchingStudents ? <Spinner size="sm" /> : 'Search'}</Button></div>
          {this.state.studentResults.length > 0 && <div className="border rounded mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>{this.state.studentResults.map((student) => (
            <Button key={student.student_id} color="light" className="w-100 text-start border-0 border-bottom rounded-0" onClick={() => this.setState((state) => ({ editor: { ...state.editor, decision_payload: { ...state.editor.decision_payload, target_student_id: student.student_id, target_student_snapshot: student } } }))}>
              <strong>{student.display_id || 'No roll'}</strong> — {student.student_name} <span className="text-muted">({student.status === '1' ? 'Active' : 'Inactive'})</span>
            </Button>
          ))}</div>}
        </div>
      );
    }
    if (field.type === 'select') {
      return <div key={field.key} className="mt-3"><Label className="fw-bold">{field.label}</Label><Input type="select" value={payload[field.key] || ''} onChange={(event) => this.updateDecisionPayload(field.key, event.target.value)}><option value="">Choose…</option>{(field.options || []).map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</Input></div>;
    }
    if (field.type === 'evidence_select') {
      const values = this.state.activeReview?.evidence?.[field.evidence_key] || [];
      return <div key={field.key} className="mt-3"><Label className="fw-bold">{field.label}</Label><Input type="select" value={payload[field.key] || ''} onChange={(event) => this.updateDecisionPayload(field.key, event.target.value)}><option value="">Choose…</option>{values.map((value) => <option key={value} value={value}>{value}</option>)}</Input></div>;
    }
    if (field.type === 'money_minor') {
      const rupees = payload[field.key] === undefined || payload[field.key] === '' ? '' : Number(payload[field.key]) / 100;
      return <div key={field.key} className="mt-3"><Label className="fw-bold">{field.label}</Label><Input type="number" min="0" step="0.01" value={rupees} onChange={(event) => this.updateDecisionPayload(field.key, event.target.value === '' ? '' : Math.round(Number(event.target.value) * 100))} /></div>;
    }
    return <div key={field.key} className="mt-3"><Label className="fw-bold">{field.label}</Label><Input type={field.type === 'integer' ? 'number' : 'text'} min={field.type === 'integer' ? 0 : undefined} value={payload[field.key] || ''} onChange={(event) => this.updateDecisionPayload(field.key, field.type === 'integer' && event.target.value !== '' ? Number(event.target.value) : event.target.value)} /></div>;
  };

  renderReviewModal = () => {
    const review = this.state.activeReview;
    if (!review) return null;
    const locked = ['approved', 'finalized'].includes(review.review_status);
    const selected = this.selectedResolution();
    const status = statusLabels[review.review_status] || statusLabels.open;
    return (
      <Modal isOpen toggle={() => this.setState({ activeReview: null })} size="xl" scrollable>
        <ModalHeader toggle={() => this.setState({ activeReview: null })}>Review: {review.subject}</ModalHeader>
        <ModalBody>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-3"><div><Badge color={severityLabels[review.severity]?.[1] || 'secondary'} className={`me-2 ${severityLabels[review.severity]?.[2] || 'bg-secondary text-white'}`}>{severityLabels[review.severity]?.[0] || review.severity}</Badge><strong>{friendlyCategory(review.category)}</strong></div><Badge color={status[1]} className={status[2]} pill>{status[0]}</Badge></div>
          <Alert color="light" className="border"><strong>What needs attention:</strong> {review.message}</Alert>
          {this.renderEvidence(review)}
          <div className="mt-4"><Label className="fw-bold">Administrator decision</Label><Select menuPortalTarget={document.body} menuPosition="fixed" styles={menuStyles} isDisabled={locked || !!this.state.working} value={selected} options={review.resolution_options || []} getOptionLabel={(option) => option.label} getOptionValue={(option) => option.value} onChange={this.chooseResolution} placeholder="Choose what should happen" /></div>
          {selected && <Alert color={selected.financial_effect === 'none' ? 'info' : 'warning'} className={selected.financial_effect === 'none' ? 'mt-2' : 'mt-2 text-dark'}><strong>{selected.label}:</strong> {selected.description}{selected.financial_effect !== 'none' && <div className="small mt-1">This choice affects how the future migration interprets financial data. It does not rewrite anything now.</div>}</Alert>}
          {(selected?.fields || []).map(this.renderDecisionField)}
          <div className="mt-3"><Label className="fw-bold">Administrator note</Label><Input type="textarea" rows={3} maxLength={1000} disabled={locked || !!this.state.working} value={this.state.editor.review_notes} placeholder="State what evidence or accounts-team confirmation supports this decision" onChange={(event) => this.setState((state) => ({ editor: { ...state.editor, review_notes: event.target.value } }))} /></div>
          <Alert color="secondary" className="mt-3 mb-0"><i className="mdi mdi-shield-lock-outline me-1" />This records a migration instruction only. Original students, fees, payments, bills, Expenses/Collections, and relief rows stay unchanged.</Alert>
          {review.review_status === 'finalized' && <Alert color="success" className="mt-3"><strong>Permanently protected.</strong> This decision is view-only and will survive a future migration rerun.{review.archive_checksum && <div className="small mt-1">Archive check: <code>{review.archive_checksum.slice(0, 12)}…</code></div>}</Alert>}
          <div className="d-flex flex-wrap gap-2 mt-4 pt-3 border-top">
            {!locked && <Button color="primary" disabled={!selected || !!this.state.working} onClick={this.saveDraft}>{this.state.working === 'draft' && <Spinner size="sm" className="me-1" />}Save as draft</Button>}
            {review.review_status === 'draft' && <Button color="info" disabled={!!this.state.working} onClick={() => this.lifecycle('approve')}>Approve decision</Button>}
            {review.review_status === 'approved' && <Button color="warning" outline disabled={!!this.state.working} onClick={() => this.lifecycle('reopen')}>Make changes</Button>}
            {review.review_status === 'approved' && <Button color="success" disabled={!!this.state.working} onClick={() => this.lifecycle('finalize')}><i className="mdi mdi-shield-lock-outline me-1" />Save permanently</Button>}
          </div>
          <details className="mt-3"><summary className="text-muted" style={{ cursor: 'pointer' }}>Show technical evidence</summary><pre className="small bg-light border rounded p-2 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(review.evidence, null, 2)}</pre></details>
        </ModalBody>
      </Modal>
    );
  };

  renderBatchModal = () => {
    if (!this.state.batchModal) return null;
    const rows = this.selectedRows();
    const options = this.commonBatchOptions();
    const selected = options.find((option) => option.value === this.state.batchEditor.resolution_code) || null;
    const activeCollectionCount = rows.reduce((sum, row) => sum + Number(row.evidence?.transaction?.active_transaction_count || 0), 0);
    const activeAmountMinor = rows.reduce((sum, row) => sum + Number(row.evidence?.transaction?.active_amount_minor || 0), 0);
    return (
      <Modal isOpen toggle={() => this.setState({ batchModal: false })} size="lg" scrollable>
        <ModalHeader toggle={() => this.setState({ batchModal: false })}>Review {rows.length} identity records together</ModalHeader>
        <ModalBody>
          <Alert color="info">
            This shortcut is available only for non-financial identity interpretations. Every row keeps its own evidence, decision, approval and permanent archive record.
          </Alert>
          <Row className="g-2 mb-3">
            <Col sm={4}><div className="border rounded p-2"><div className="small text-muted">Selected records</div><strong>{rows.length}</strong></div></Col>
            <Col sm={4}><div className="border rounded p-2"><div className="small text-muted">Active collections referenced</div><strong>{activeCollectionCount}</strong></div></Col>
            <Col sm={4}><div className="border rounded p-2"><div className="small text-muted">Preserved collection amount</div><strong>{formatMoney(activeAmountMinor / 100)}</strong></div></Col>
          </Row>
          <div className="border rounded mb-3" style={{ maxHeight: 240, overflowY: 'auto' }}>
            <Table size="sm" hover className="mb-0">
              <thead className="table-light"><tr><th>Record</th><th>Category</th><th className="text-end">Collections</th></tr></thead>
              <tbody>{rows.map((row) => <tr key={row.review_id}><td>{row.subject}</td><td>{friendlyCategory(row.category)}</td><td className="text-end">{row.evidence?.transaction?.active_transaction_count || 0}</td></tr>)}</tbody>
            </Table>
          </div>
          <Label className="fw-bold">Decision for every selected record</Label>
          <Select
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={menuStyles}
            value={selected}
            options={options}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            onChange={(choice) => this.setState((state) => ({ batchEditor: { ...state.batchEditor, resolution_code: choice?.value || '' } }))}
          />
          {selected && <Alert color="info" className="mt-2"><strong>{selected.label}:</strong> {selected.description}</Alert>}
          <div className="mt-3">
            <Label className="fw-bold">Administrator note applied to each selected record</Label>
            <Input type="textarea" rows={3} maxLength={1000} value={this.state.batchEditor.review_notes} placeholder="State the common evidence or accounts-team confirmation supporting these decisions" onChange={(event) => this.setState((state) => ({ batchEditor: { ...state.batchEditor, review_notes: event.target.value } }))} />
          </div>
          <div className="d-flex gap-2 mt-4 pt-3 border-top">
            <Button color="primary" disabled={!selected || this.state.batchEditor.review_notes.trim().length < 3 || !!this.state.working} onClick={this.saveBatchDraft}>
              {this.state.working === 'batch-draft' && <Spinner size="sm" className="me-1" />}Save {rows.length} separate drafts
            </Button>
            <Button color="secondary" outline disabled={!!this.state.working} onClick={() => this.setState({ batchModal: false })}>Cancel</Button>
          </div>
        </ModalBody>
      </Modal>
    );
  };

  renderRows = () => {
    if (this.props.mode === 'summary') return null;
    const selectedRows = this.selectedRows();
    const selectedStatuses = new Set(selectedRows.map((row) => row.review_status));
    const allEligibleOnPage = this.state.rows.filter((row) => row.review_status !== 'finalized' && (row.batch_resolution_options || []).length);
    const allEligibleSelected = allEligibleOnPage.length > 0 && allEligibleOnPage.every((row) => this.state.selectedReviewIds.includes(row.review_id));
    const categoryOptions = (this.state.summary?.categories || []).filter((row) => row.review_group === this.props.mode);
    const remainingInQueue = Number(this.state.summary?.remaining_by_group?.[this.props.mode] || 0);
    return (
      <Card><CardBody>
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
          <div><h5 className="mb-1">{this.props.mode === 'identity' ? 'Match historical student identities' : 'Resolve historical exceptions'}</h5><div className="text-muted">{remainingInQueue > 0 ? `${remainingInQueue} of ${this.state.total} records still need a decision.` : `All ${this.state.total} records in this queue are saved permanently.`}</div></div>
          <div className="d-flex flex-wrap gap-2">
            <Input type="select" style={{ width: 240 }} value={this.state.category} onChange={(event) => this.setState({ category: event.target.value, page: 1 }, () => this.loadRows(1))}><option value="">All issue types</option>{categoryOptions.map((row) => <option key={row.category} value={row.category}>{friendlyCategory(row.category)} ({row.remaining})</option>)}</Input>
            <Input type="select" style={{ width: 160 }} value={this.state.severity} onChange={(event) => this.setState({ severity: event.target.value, page: 1 }, () => this.loadRows(1))}><option value="">All priorities</option><option value="blocking">Must resolve</option><option value="high">Important</option><option value="medium">Check</option></Input>
            <Input type="select" style={{ width: 170 }} value={this.state.status} onChange={(event) => this.setState({ status: event.target.value, page: 1 }, () => this.loadRows(1))}><option value="">All statuses</option><option value="open">Needs review</option><option value="draft">Draft</option><option value="approved">Approved</option><option value="finalized">Saved permanently</option></Input>
            <Input style={{ width: 260 }} value={this.state.search} onChange={(event) => this.setState({ search: event.target.value })} onKeyDown={(event) => { if (event.key === 'Enter') this.loadRows(1); }} placeholder="Search roll, issue or evidence" />
            <Button color="secondary" outline onClick={() => this.loadRows(1)}>Search</Button>
          </div>
        </div>
        {selectedRows.length > 0 && <Alert color="info" className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div><strong>{selectedRows.length} selected.</strong> Batch actions still create and protect one decision per record.</div>
          <div className="d-flex flex-wrap gap-2">
            {[...selectedStatuses].every((status) => ['open', 'draft'].includes(status)) && <Button size="sm" color="primary" disabled={!this.commonBatchOptions().length || !!this.state.working} onClick={this.openBatchDraft}>Set selected decisions</Button>}
            {selectedStatuses.size === 1 && selectedStatuses.has('draft') && <Button size="sm" color="info" disabled={!!this.state.working} onClick={() => this.batchLifecycle('approve')}>Approve selected</Button>}
            {selectedStatuses.size === 1 && selectedStatuses.has('approved') && <Button size="sm" color="success" disabled={!!this.state.working} onClick={() => this.batchLifecycle('finalize')}><i className="mdi mdi-shield-lock-outline me-1" />Save selected permanently</Button>}
            <Button size="sm" color="secondary" outline disabled={!!this.state.working} onClick={() => this.setState({ selectedReviewIds: [] })}>Clear</Button>
          </div>
        </Alert>}
        {this.state.loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : this.state.rows.length ? (
          <Table responsive hover className="align-middle mb-0"><thead className="table-light"><tr><th style={{ width: 40 }}><input type="checkbox" aria-label="Select all batch-safe records on this page" checked={allEligibleSelected} disabled={!allEligibleOnPage.length} onChange={this.togglePageSelection} /></th><th>Priority</th><th>Record</th><th>What needs attention</th><th>Status</th><th /></tr></thead><tbody>{this.state.rows.map((review) => {
            const status = statusLabels[review.review_status] || statusLabels.open;
            const severity = severityLabels[review.severity] || [review.severity, 'secondary'];
            const batchSafe = review.review_status !== 'finalized' && (review.batch_resolution_options || []).length > 0;
            const recommendedRoll = review.evidence?.student_match?.target_source_roll;
            return <tr key={review.review_id}><td><input type="checkbox" aria-label={`Select ${review.subject}`} checked={this.state.selectedReviewIds.includes(review.review_id)} disabled={!batchSafe} title={batchSafe ? 'Select for a non-financial batch decision' : 'This record requires individual review'} onChange={() => this.toggleReviewSelection(review.review_id)} /></td><td><Badge color={severity[1]} className={severity[2] || 'bg-secondary text-white'}>{severity[0]}</Badge></td><td><div className="fw-bold">{review.subject}</div>{recommendedRoll && <div className="small text-success">Recommended: {recommendedRoll}</div>}<div className="small text-muted">{friendlyCategory(review.category)}</div></td><td style={{ minWidth: 280 }}>{review.message}</td><td><Badge color={status[1]} className={status[2]} pill>{status[0]}</Badge></td><td className="text-end"><Button color="primary" outline size="sm" onClick={() => this.openReview(review)}>{review.review_status === 'finalized' ? 'View' : 'Review'}</Button></td></tr>;
          })}</tbody></Table>
        ) : <Alert color="success" className="mb-0">No review records match this filter.</Alert>}
        <div className="d-flex justify-content-between align-items-center mt-3"><div className="small text-muted">Page {this.state.page} of {this.state.totalPages}</div><div className="d-flex gap-2"><Button size="sm" color="secondary" outline disabled={this.state.page <= 1 || this.state.loading} onClick={() => this.loadRows(this.state.page - 1)}>Previous</Button><Button size="sm" color="secondary" outline disabled={this.state.page >= this.state.totalPages || this.state.loading} onClick={() => this.loadRows(this.state.page + 1)}>Next</Button></div></div>
      </CardBody></Card>
    );
  };

  render() {
    return <>{this.renderSummary()}{this.renderRows()}{this.renderReviewModal()}{this.renderBatchModal()}</>;
  }
}

export default SemanticReviewPanel;
